import { HorizBox, Pad, VertBox } from 'component/basics';
import {
    AccordionField,
    Checkbox,
    DropdownField,
    EditingCard,
    FormField,
    ListSelector,
    PlaceholderContainer,
    RadioGroup,
    RadioOption,
    SaveableTextField,
    SearchField,
    Toggle,
} from 'component/form';
import { CircleCount, TextArea, TextField, UtilityText } from 'component/text';
import { useState } from 'react';
import { View } from 'react-native';
import { CLICK, component, demoFile, INPUT, POPUP, story, variant, WITHIN } from 'system/demo';
import { sortByOrder } from 'util/sort-by-order';
import { t } from 'util/stdtypes';
import { formatString } from 'util/util';

demoFile('component/form', {
    designUrl: 'https://www.figma.com/design/MX0AcO8d0ZlCBs4e9vkl5f/PSI-Design-System?node-id=1415-5890&t=MC9nppcf9h2iJDKP-1',
});

function FormFieldDemo() {
    const [text, setText] = useState('');
    const [isEditing, setIsEditing] = useState(false);

    return (
        <VertBox gap={16} wide>
            <FormField text='Field label'>
                <TextField multiline onChange={setText} placeholderLabel={t('!Text Field')} value={text} />
            </FormField>
            <FormField required text='Required Field'>
                <TextField multiline onChange={setText} placeholderLabel={t('!Text Field')} value={text} />
            </FormField>
            <FormField descriptionText='Description appears below' text='Field with Description'>
                <TextField multiline onChange={setText} placeholderLabel={t('!Text Field')} value={text} />
            </FormField>
            <FormField errorText='This is an error' text='Field with Error'>
                <TextField error multiline placeholderLabel={t('!Text Field')} value='Invalid Text' />
            </FormField>
            <FormField text='Small Field'>
                <TextField multiline={false} onChange={setText} placeholderLabel={t('!Text Field')} size='small' value={text} />
            </FormField>
            <FormField text='Multiline Field'>
                <TextArea onChange={setText} placeholderText='Multiline textarea. Automatically `multiline` and `big`.' value={text} />
            </FormField>
        </VertBox>
    );
}

component('FormField', FormFieldDemo);

function DropdownFieldDemo() {
    const [value, setValue] = useState(null as string | null);
    const options = [
        { key: 'a', label: t('!Option 1') },
        { key: 'b', label: t('!Option 2') },
    ];
    return (
        <VertBox wide>
            <DropdownField onChange={setValue} options={options} placeholder={t('!Select an option')} testID='dropdown' value={value} />
            <Pad size={16} />
            <DropdownField
                disabled
                onChange={setValue}
                options={options}
                placeholder={t('!Select an option')}
                testID='disabled-dropdown'
                value={value}
            />
        </VertBox>
    );
}
component('DropdownField', DropdownFieldDemo, {}, [story('Option 1', [CLICK('dropdown'), POPUP(CLICK('a'))])]);

function SearchFieldDemo() {
    const [text, setText] = useState(null);
    return (
        <VertBox>
            <SearchField onChange={setText} placeholder={t('!Search Field')} value={text} />
            <Pad size={16} />
            <SearchField onChange={setText} placeholder={t('!Small Search Field')} size='small' value={text} />
        </VertBox>
    );
}

component('SearchField', SearchFieldDemo);

function ToggleDemo() {
    const [switchValue, setSwitchValue] = useState(false);

    return (
        <VertBox gap={16}>
            <Toggle onChange={setSwitchValue} testID='toggle' text='Toggle' value={switchValue} />
            <Toggle emoji='😭' onChange={setSwitchValue} text='Toggle with emoji' value={switchValue} />
            <Toggle emoji='😭' onChange={setSwitchValue} spread text='Spread Toggle' value={switchValue} />
            <Toggle
                onChange={setSwitchValue}
                text='This is a toggle with a very long label that needs to be wrapped onto another screen in order to not look bag'
                value={switchValue}
            />
            <Toggle
                infoText='This is a toggle with some informational text.'
                onChange={setSwitchValue}
                text='Toggle with info'
                value={switchValue}
            />
        </VertBox>
    );
}

component('Toggle', ToggleDemo, {}, [story('Toggle', [CLICK('toggle')])]);

function CheckboxDemo() {
    const [switchValue, setSwitchValue] = useState(false);
    return (
        <VertBox>
            <Checkbox onChange={setSwitchValue} text='Checkbox' value={switchValue} />
            <Checkbox emoji='🔥' onChange={setSwitchValue} text='Checkbox with emoji' value={switchValue} />
        </VertBox>
    );
}

component('Checkbox', CheckboxDemo);

function RadioGroupDemo() {
    const [radioValue, setRadioValue] = useState('cat');
    return (
        <View>
            <RadioGroup onChange={setRadioValue} value={radioValue}>
                <RadioOption radioKey='cat' text='Cat' />
                <RadioOption radioKey='dog' text='Dog' />
                <RadioOption radioKey='Komodo Dragon' text='Komodo Dragon' />
            </RadioGroup>
            <Pad />
            <UtilityText text={formatString('Selected: {value}', { value: radioValue })} />
        </View>
    );
}

component('RadioGroup', RadioGroupDemo, {}, [story('Dog', [CLICK('Dog')])]);

function AccordionFieldDemo() {
    return (
        <View>
            <AccordionField testID='accordion' titleContent={<UtilityText text='Title' weight='medium' />}>
                <UtilityText text='Content' />
            </AccordionField>
            <AccordionField
                titleContent={
                    <HorizBox center>
                        <UtilityText text='With Count' weight='medium' />
                        <Pad size={8} />
                        <CircleCount count={3} />
                    </HorizBox>
                }
            >
                <UtilityText text='Content' />
            </AccordionField>
        </View>
    );
}

component('AccordionField', AccordionFieldDemo, {}, [story('Expand', [CLICK('accordion')])]);

function SaveableTextFieldDemo() {
    const [value, setValue] = useState('Initial Value');
    const [valueWithPlaceholder, setValueWithPlaceholder] = useState(null as string | null);
    return (
        <View>
            <SaveableTextField label={t('!Saveable Text Field')} onSave={setValue} testID='saveable-text-field' value={value} />
            <Pad size={16} />
            <SaveableTextField
                label={t('!Saveable Text Field with Placeholder')}
                onSave={setValueWithPlaceholder}
                placeholder={t('!Placeholder')}
                testID='with-placeholder'
                value={valueWithPlaceholder}
            />
        </View>
    );
}

component('SaveableTextField', SaveableTextFieldDemo, {}, [
    story('Edit and Save', [CLICK('saveable-text-field-edit'), INPUT('saveable-text-field', 'New Value'), CLICK('Save')]),
    story('Edit and Cancel', [CLICK('saveable-text-field-edit'), INPUT('saveable-text-field', 'New Value'), CLICK('Cancel')]),
]);

function PlaceholderContainerDemo() {
    return (
        <PlaceholderContainer>
            <UtilityText text='Placeholder' />
        </PlaceholderContainer>
    );
}

component('PlaceholderContainer', PlaceholderContainerDemo);

function EditingCardDemo() {
    return (
        <EditingCard>
            <UtilityText text='Editing Card' />
        </EditingCard>
    );
}

component('EditingCard', EditingCardDemo);

function ListSelectorDemo() {
    const [selectedItems, setSelectedItems] = useState([
        { key: 'a', text: 'Item 1' },
        { key: 'b', text: 'Item 2' },
        { key: 'c', text: 'Item 3' },
    ]);

    function handleChangeSortOrder(orderedKeys: string[]) {
        setSelectedItems((prev) => sortByOrder(prev, orderedKeys));
    }

    function AddWidget({ setToAdd }) {
        const [selected, setSelected] = useState(false);
        return (
            <Checkbox
                onChange={(selected) => {
                    setToAdd(selected ? ['4'] : null);
                    setSelected(selected);
                }}
                text='Item 4'
                value={selected}
            />
        );
    }

    return (
        <ListSelector<string>
            addHintLabel={t('!Add Item Hint')}
            addLabel={t('!Add Item')}
            addWidget={AddWidget}
            items={selectedItems}
            label={t('!List Selector')}
            onAddItems={async (newItemKeys) => {
                const newItems = newItemKeys.map((key) => ({
                    key,
                    text: `Item ${key}`,
                }));
                setSelectedItems((prev) => [...prev, ...newItems]);
            }}
            onChangeSortOrder={handleChangeSortOrder}
            onDeleteItem={(key) => setSelectedItems((prev) => prev.filter((item) => item.key !== key))}
            reorderable
        />
    );
}

component('ListSelector', ListSelectorDemo, {}, [
    story('Move Item down', [WITHIN('Item 1', CLICK('down'))]),
    story('Move Item up', [WITHIN('Item 2', CLICK('up'))]),
]);

function ListSelectorWithErrorDemo() {
    const [selectedItems, setSelectedItems] = useState([
        { key: 'a', text: 'Item 1' },
        { key: 'b', text: 'Item 2' },
    ]);

    function handleChangeSortOrder(orderedKeys: string[]) {
        setSelectedItems((prev) => sortByOrder(prev, orderedKeys));
    }

    function AddWidget({ setToAdd }) {
        const [good, setGood] = useState(false);
        const [bad, setBad] = useState(false);

        function updateToAdd(newGood, newBad) {
            const itemsToAdd: string[] = [];
            if (newGood) itemsToAdd.push('3');
            if (newBad) itemsToAdd.push('error');
            setToAdd(itemsToAdd.length > 0 ? itemsToAdd : []);
        }

        return (
            <View>
                <Checkbox
                    onChange={(checked) => {
                        setGood(checked);
                        updateToAdd(checked, bad);
                    }}
                    text='Item 3 (will succeed)'
                    value={good}
                />
                <Pad size={12} />
                <Checkbox
                    onChange={(checked) => {
                        setBad(checked);
                        updateToAdd(good, checked);
                    }}
                    text='Trigger error on add'
                    value={bad}
                />
            </View>
        );
    }

    return (
        <ListSelector<string>
            addHintLabel={t('!Test error handling')}
            addLabel={t('!Add Item')}
            addWidget={AddWidget}
            items={selectedItems}
            label={t('!List Selector with Error Handling')}
            onAddItems={async (newItemKeys) => {
                // Simulate an error when trying to add an item with key 'error'
                if (newItemKeys.includes('error')) {
                    return { error: t('!Failed to add item. This is a test error message.') };
                }
                const newItems = newItemKeys.map((key) => ({
                    key,
                    text: `Item ${key}`,
                }));
                setSelectedItems((prev) => [...prev, ...newItems]);
                return { error: null };
            }}
            onChangeSortOrder={handleChangeSortOrder}
            onDeleteItem={(key) => setSelectedItems((prev) => prev.filter((item) => item.key !== key))}
            reorderable
        />
    );
}

component('ListSelector with Error', ListSelectorWithErrorDemo, {}, [
    story('Add item successfully', [CLICK('!Add Item'), CLICK('Item 3 (will succeed)'), CLICK('Add')]),
    story('Trigger error on add', [CLICK('!Add Item'), CLICK('Trigger error on add'), CLICK('Add')]),
]);
